import {
  useEffect,
  useRef,
  useState,
  type JSX,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from '../../shared/i18n';
import { NODE_KIND_LABEL_KEYS, NodeIcon } from '../../shared/ui/NodeIcon';
import type { Status } from '../../shared/ui/StatusDot';
import {
  clampToScene,
  linkGeometry,
  sceneScale,
  DEMO_LINKS,
  DEMO_NODE,
  DEMO_NODES,
  DEMO_SCENE,
  type DemoNode,
  type Point,
} from './demoScene';

/**
 * Демо-схема сети на экране входа.
 *
 * Смысл у неё один — показать, чем платформа занимается, ещё до входа:
 * карточки узлов с состоянием, связи, рамка площадки. Объекты
 * двигаются мышью, потому что неподвижная схема ничем не отличается
 * от скриншота.
 *
 * Написана без React Flow намеренно. Библиотека карты вынесена в отдельный
 * кусок бандла ровно затем, чтобы её не грузил экран входа (см. App.tsx),
 * и втащить её обратно ради украшения значило бы отменить эту экономию.
 */
export function DemoMap({ className }: { className?: string }): JSX.Element {
  const { t } = useTranslation();

  const frame = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const [positions, setPositions] = useState<Record<string, Point>>(() =>
    Object.fromEntries(DEMO_NODES.map((node) => [node.id, node.at])),
  );

  const [dragged, setDragged] = useState<{ id: string; pointer: Point; origin: Point } | null>(
    null,
  );

  // Схема нарисована в своих координатах и занимает свою долю правой зоны:
  // масштаб — единственное, что зависит от размера окна.
  useEffect(() => {
    const element = frame.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const size = entries[0]?.contentRect;

      if (!size || size.width === 0) {
        return;
      }

      setScale(sceneScale(size.width, size.height));
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const at = (node: DemoNode): Point => positions[node.id] ?? node.at;

  return (
    <div
      ref={frame}
      className={`flex items-center justify-center overflow-hidden ${className ?? ''}`}
      role="img"
      aria-label={t('auth.demo.label')}
    >
      <div
        className="relative flex-none"
        style={{
          width: DEMO_SCENE.width,
          height: DEMO_SCENE.height,
          transform: `scale(${scale})`,
        }}
      >
        {/*
          Рамка площадки — как группа на основной карте: пунктир, заголовок
          с числом объектов. Она же граница, за которую карточку не вытянуть.
        */}
        <div className="absolute inset-0 rounded-lg border border-dashed border-line-strong bg-surface/40">
          <div className="flex items-center gap-2 border-b border-line px-2.5 py-1">
            <span className="truncate text-sm font-medium">{t('auth.demo.site')}</span>
            <span className="ml-auto flex-none font-mono text-2xs text-dim">
              {t('common.count.nodes', { count: DEMO_NODES.length })}
            </span>
          </div>
        </div>

        <svg
          className="pointer-events-none absolute inset-0"
          width={DEMO_SCENE.width}
          height={DEMO_SCENE.height}
          aria-hidden="true"
        >
          {DEMO_LINKS.map((link) => {
            const ends = endsOf(link.from, link.to);

            if (!ends) {
              return null;
            }

            return (
              <path
                key={`${link.from}-${link.to}`}
                d={linkGeometry(at(ends.from), at(ends.to)).path}
                fill="none"
                stroke="var(--color-line-strong)"
                strokeWidth={1.5}
              />
            );
          })}
        </svg>

        {/*
          Подписи связей лежат поверх линий отдельным слоем, а не внутри SVG:
          на основной карте это тоже HTML-плашка со своей подложкой, иначе
          линия читается сквозь текст.
        */}
        {DEMO_LINKS.map((link) => {
          const ends = endsOf(link.from, link.to);

          if (!ends || !link.label) {
            return null;
          }

          const { label } = linkGeometry(at(ends.from), at(ends.to));

          return (
            <div
              key={`${link.from}-${link.to}-label`}
              className="pointer-events-none absolute rounded-sm border border-line bg-raised px-1 py-px font-mono leading-none text-muted shadow-sm"
              style={{
                left: label.x,
                top: label.y,
                transform: 'translate(-50%, -50%)',
                fontSize: 9,
              }}
            >
              {link.label}
            </div>
          );
        })}

        {DEMO_NODES.map((node) => (
          <Card
            key={node.id}
            node={node}
            at={at(node)}
            dragging={dragged?.id === node.id}
            onDragStart={(pointer) => {
              setDragged({ id: node.id, pointer, origin: at(node) });
            }}
            onDragMove={(pointer) => {
              if (dragged?.id !== node.id) {
                return;
              }

              // Курсор двигается в пикселях экрана, схема — в своих: без
              // деления на масштаб карточка обгоняла бы курсор.
              setPositions((previous) => ({
                ...previous,
                [node.id]: clampToScene({
                  x: dragged.origin.x + (pointer.x - dragged.pointer.x) / scale,
                  y: dragged.origin.y + (pointer.y - dragged.pointer.y) / scale,
                }),
              }));
            }}
            onDragEnd={() => setDragged(null)}
          />
        ))}
      </div>
    </div>
  );
}

/** Узлы на концах связи. Связь с несуществующим узлом просто не рисуется. */
function endsOf(from: string, to: string): { from: DemoNode; to: DemoNode } | null {
  const source = DEMO_NODES.find((node) => node.id === from);
  const target = DEMO_NODES.find((node) => node.id === to);

  return source && target ? { from: source, to: target } : null;
}

interface CardProps {
  node: DemoNode;
  at: Point;
  dragging: boolean;
  onDragStart: (pointer: Point) => void;
  onDragMove: (pointer: Point) => void;
  onDragEnd: () => void;
}

/** Карточка узла — уменьшенная копия узла основной карты (раздел 03 макета). */
function Card({ node, at, dragging, onDragStart, onDragMove, onDragEnd }: CardProps): JSX.Element {
  const { t } = useTranslation();

  const tone = TONES[node.status];

  return (
    <div
      className={[
        'absolute flex touch-none flex-col justify-center rounded-md px-2.5',
        'bg-raised text-fg shadow-sm select-none',
        tone.border,
        node.status === 'down' ? 'border-2' : 'border',
        dragging ? 'cursor-grabbing ring-2 ring-info' : 'cursor-grab',
      ].join(' ')}
      style={{
        left: at.x,
        top: at.y,
        width: DEMO_NODE.width,
        height: DEMO_NODE.height,
        // Взятая карточка идёт поверх остальных: иначе она ныряет под соседа
        // ровно в тот момент, когда её к нему тянут.
        zIndex: dragging ? 2 : 1,
      }}
      onPointerDown={(event: ReactPointerEvent<HTMLDivElement>) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        onDragStart({ x: event.clientX, y: event.clientY });
      }}
      onPointerMove={(event: ReactPointerEvent<HTMLDivElement>) => {
        onDragMove({ x: event.clientX, y: event.clientY });
      }}
      onPointerUp={onDragEnd}
      onPointerCancel={onDragEnd}
    >
      <div className="flex items-center gap-1.5">
        {/*
          Состояние кодируется цветом и знаком внутри точки — то же правило
          §12, что и на основной карте: один только цвет читают не все.
        */}
        <span
          className={`flex h-3 w-3 flex-none items-center justify-center rounded-full text-[8px] leading-none font-bold text-bg ${tone.dot}`}
          aria-hidden="true"
        >
          {tone.sign}
        </span>
        <span className="truncate text-sm font-medium">{t(node.name)}</span>
        <span className="ml-auto flex-none text-dim" title={t(NODE_KIND_LABEL_KEYS[node.kind])}>
          <NodeIcon kind={node.kind} />
        </span>
      </div>

      <div className="truncate font-mono text-2xs text-dim">{node.address}</div>

      <div
        className={`truncate font-mono text-2xs ${node.status === 'down' ? 'text-down-fg' : 'text-faint'}`}
      >
        {t(node.summary)}
      </div>
    </div>
  );
}

const TONES: Record<Status, { border: string; dot: string; sign: string }> = {
  up: { border: 'border-up', dot: 'bg-up', sign: '' },
  down: { border: 'border-down', dot: 'bg-down', sign: '×' },
  warn: { border: 'border-warn', dot: 'bg-warn', sign: '!' },
  unknown: { border: 'border-line-strong', dot: 'bg-dim', sign: '?' },
};
